import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { DrawerContent, SwipeableDrawer } from "@/components/Drawer";
import { IconButton } from "@/components/IconButton/IconButton";
import { Radio } from "@/components/Radio/Radio";
import { RadioGroup } from "@/components/Radio/RadioGroup";
import { TextField } from "@/components/TextField/TextField";
import DeleteTraveller from "@/features/travellers/components/DeleteTraveller";
import { TravellerFormDrawer } from "@/features/travellers/components/TravellerFormDrawer";
import { useDeleteTravellerMutation } from "@/features/travellers/hooks/useTravellers";
import type { Traveller } from "@/features/travellers/types/types";
import {
  getTravellerAgeAtTravelDate,
  getTravellerTypeAtTravelDate,
} from "@/features/travellers/utils/ageValidation";
import { getTravelerType } from "@/features/travellers/utils/transformTraveler";
import { rudderAnalytics, CommonTrackingEvents } from "@/lib/rudderstack";
import { numericDateFormatOptions } from "@/utils/dates";

import { TravelerTypeLabel } from "./TravelerTypeLabel";
import { TravellerActions } from "./TravellerActions";

type Props = {
  travellers: Traveller[];
  travelDate: Date;
  selectedTravellerId: string | null;
  onTravellerSelected: (travellerId: string) => void;
  isSelectionDisabled?: (traveller: Traveller) => boolean;
};

export function TravellersList({
  travellers,
  travelDate,
  selectedTravellerId,
  onTravellerSelected,
  isSelectionDisabled,
}: Props) {
  const [travellerForDeletion, setTravellerForDeletion] =
    useState<Traveller | null>(null);
  const [travellerForEdit, setTravellerForEdit] = useState<Traveller | null>(
    null,
  );
  const [newTraveller, setNewTraveller] = useState(false);
  const intl = useIntl();

  const { mutateAsync: deleteTraveller } = useDeleteTravellerMutation();
  const [query, setQuery] = useState("");

  const initTravellerDeletion = (traveller: Traveller) => {
    setTravellerForDeletion(traveller);
  };

  const initTravellerEdit = (traveller: Traveller) => {
    setTravellerForEdit(traveller);
  };

  const closeDrawerHandler = () => {
    setTravellerForDeletion(null);
    setTravellerForEdit(null);
    setNewTraveller(false);
  };

  const filteredTravellers = useMemo(
    () =>
      travellers.filter((traveller) =>
        `${traveller.firstName.toLocaleLowerCase()} ${traveller.lastName.toLocaleLowerCase()}`.includes(
          query.toLocaleLowerCase(),
        ),
      ),
    [query, travellers],
  );

  return (
    <>
      <div className="section-card mb-4 text-center">
        <p className="mb-1 text-base font-medium text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Traveller not on the list?",
            description:
              "Heading shown when a searched traveller is not found in the saved list",
          })}
        </p>
        <p className="text-sm font-normal text-text-secondary">
          {intl.formatMessage({
            defaultMessage: "Add them manually in a few steps",
            description:
              "Explanatory text when a searched traveller is not found in the saved list",
          })}
        </p>
        <div className="mt-4 mb-2">
          <Button
            variant="outlined"
            size="small"
            color="primary"
            fullWidth
            onClick={() => {
              setNewTraveller(true);
              rudderAnalytics.track(
                CommonTrackingEvents.USER_ENTERED_CREATING_TRAVELER,
              );
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Add new traveller",
              description: "Button to create a new traveller profile",
            })}
          </Button>
        </div>
      </div>
      <div className="section-title">
        {intl.formatMessage({
          defaultMessage: "Saved Travellers",
          description:
            "Section heading above the list of previously saved traveller profiles",
        })}
      </div>
      <TextField
        placeholder={intl.formatMessage({
          defaultMessage: "Search by name",
          description:
            "Placeholder or label for the traveller search-by-name input field",
        })}
        fullWidth
        size="small"
        startAdornment={
          <MagnifyingGlassIcon
            size={20}
            color="var(--color-text-secondary)"
            weight="bold"
            className="me-2"
          />
        }
        endAdornment={
          query ? (
            <IconButton onClick={() => setQuery("")}>
              <XIcon size={20} color="var(--color-stone)" weight="bold" />
            </IconButton>
          ) : null
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="mt-2">
        {filteredTravellers.length > 0 ? (
          <div className="rounded-2xl bg-white">
            <RadioGroup
              value={selectedTravellerId || ""}
              onChange={(e, travellerId) => {
                onTravellerSelected(travellerId);
              }}
            >
              {filteredTravellers?.map((traveller) => {
                // block the traveller selection if the first travel date (check-in/departure)
                // is less than 6 months from the passport expiry date
                const isExpired = dayjs(travelDate)
                  .add(6, "months")
                  .isAfter(dayjs(traveller.passportExpiryDate));

                const ageGroupAtTravelDate = getTravellerTypeAtTravelDate(
                  traveller.birthDate,
                  travelDate,
                );

                const ageGroup = getTravelerType(traveller.birthDate);

                const isDisabled =
                  isExpired || isSelectionDisabled?.(traveller);

                return (
                  <div
                    key={traveller.id}
                    className="flex justify-between gap-2 border-b border-stroke-neutral-soft px-2 py-4 last:border-b-0"
                  >
                    <Radio
                      value={traveller.id}
                      className="m-0 flex gap-2"
                      onChange={(e) => {
                        rudderAnalytics.track(
                          e.target.checked
                            ? CommonTrackingEvents.USER_SELECTED_TRAVELER
                            : CommonTrackingEvents.USER_DESELECTED_TRAVELER,
                          {
                            traveler_type: getTravelerType(traveller.birthDate),
                            traveler_type_at_travel_date:
                              getTravellerTypeAtTravelDate(
                                traveller.birthDate,
                                travelDate,
                              ),
                          },
                        );
                      }}
                      disabled={isDisabled}
                      label={
                        <div className="flex flex-1 flex-col" data-sentry-mask>
                          <span
                            className={clsx(
                              "text-base font-medium",
                              isDisabled
                                ? "text-text-disabled-primary"
                                : "text-text-primary",
                            )}
                          >
                            {traveller.firstName} {traveller.lastName}
                          </span>

                          <span
                            className={clsx(
                              "text-sm",
                              isDisabled
                                ? "text-text-disabled-secondary"
                                : "text-text-secondary",
                            )}
                          >
                            <TravelerTypeLabel {...traveller} />{" "}
                            <span className="text-stroke-neutral-medium">
                              &bull;
                            </span>{" "}
                            {intl.formatDate(
                              traveller.birthDate,
                              numericDateFormatOptions,
                            )}
                          </span>

                          {isExpired ? (
                            <span className="mt-0.5 text-xs text-semantic-error-strong">
                              {intl.formatMessage({
                                defaultMessage:
                                  "Passport expires in less than 6 months from the date of check-in/departure",
                                description:
                                  "Hint text explaining passport expiry date requirements for travel",
                              })}
                            </span>
                          ) : ageGroup !== ageGroupAtTravelDate ? (
                            <span className="mt-0.5 text-xs text-semantic-warning-strong">
                              {intl.formatMessage(
                                {
                                  defaultMessage:
                                    "Traveller will be {age} years old at travel date, which makes them counted as {ageGroupAtTravelDate, select, child {a Child} infant {an Infant} other {an Adult}}",
                                  description:
                                    "Error shown when a child traveller's age is not valid for the selected booking",
                                },
                                {
                                  ageGroupAtTravelDate,
                                  age: getTravellerAgeAtTravelDate(
                                    traveller.birthDate,
                                    travelDate,
                                  ),
                                },
                              )}
                            </span>
                          ) : null}
                        </div>
                      }
                      labelPlacement="end"
                    />

                    <div className="flex shrink-0 items-center">
                      <TravellerActions
                        traveller={traveller}
                        onDelete={initTravellerDeletion}
                        onEdit={initTravellerEdit}
                      />
                    </div>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 py-10">
            <div className="font-medium text-black">
              {intl.formatMessage({
                defaultMessage: "Nothing found",
                description:
                  "Message shown when a traveller name search returns no results",
              })}
            </div>
            <div className="text-center text-sm text-text-placeholder">
              {query
                ? intl.formatMessage({
                    defaultMessage: "Please check your input for typos",
                    description:
                      "Hint to check for typos when a traveller name search returns no results",
                  })
                : intl.formatMessage({
                    defaultMessage: "No saved travellers to choose from",
                    description:
                      "Message shown when the user has no saved traveller profiles",
                  })}
            </div>
          </div>
        )}
      </div>

      <TravellerFormDrawer
        open={Boolean(newTraveller || travellerForEdit)}
        onClose={closeDrawerHandler}
        traveller={travellerForEdit}
      />

      <SwipeableDrawer
        open={Boolean(travellerForDeletion)}
        onClose={() => setTravellerForDeletion(null)}
      >
        <DrawerContent>
          {travellerForDeletion ? (
            <DeleteTraveller
              traveller={travellerForDeletion}
              deleteTraveller={() => deleteTraveller(travellerForDeletion.id)}
              closeDrawer={closeDrawerHandler}
            />
          ) : null}
        </DrawerContent>
      </SwipeableDrawer>
    </>
  );
}
